import { unescape as htmlUnescape } from 'html-escaper';
import { uniqueId } from 'lodash';
import striptags from 'striptags';
import { replaceHTMLTagBackToMarkDownTag, replacerMarkDownTagToHTMLTag } from './strings';

const nunjucks = require('nunjucks');
// NOTE: Turning off autoescaping can be dangerous
nunjucks.configure('./', { autoescape: false });

const markDownRegex = [
  {
    regexString: '\\w+([\\.-]?\\w+)*@\\w+([\\.-]?\\w+)*(\\.\\w{2,3})+',
    htmlTagString: 'emailAddress'
  },
  {
    regexString: '\\b(?:https?|ftp)://[a-z0-9-+&@#/%?=~_|!:,.;]*[a-z0-9-+&@#/%=~_|]',
    htmlTagString: 'urlLink'
  }
];

function parseFromString(inputString) {
  let replacedContent = null;

  const parser = new DOMParser();

  markDownRegex.forEach((replaceObj) => {
    replacedContent = replacerMarkDownTagToHTMLTag(
      inputString,
      replaceObj.regexString,
      replaceObj.htmlTagString
    );
  });

  return parser.parseFromString(replacedContent, 'text/html');
}

function unescape(inputString) {
  let replacedContent = null;

  markDownRegex.forEach((replaceObj) => {
    replacedContent = replaceHTMLTagBackToMarkDownTag(
      inputString,
      replaceObj.regexString,
      replaceObj.htmlTagString
    );
  });

  return htmlUnescape(replacedContent);
}

function removeTooltipSpanTag(text) {
  const parser = new DOMParser();
  const parsedHtml = parser.parseFromString(text, 'text/html');

  const tagElements = parsedHtml.getElementsByTagName('span');

  if (tagElements && tagElements.length > 0) {
    Array.prototype.forEach.call(tagElements, (tagElement) => {
      if (
        tagElement.className === 'tooltip-text' &&
        tagElement.innerHTML === `This value can't be edited.`
      ) {
        tagElement.remove();
      }
    });
  }

  return parsedHtml.body.innerHTML;
}

function getCreatorId(navigation, cemoduleId, pagePath) {
  const navObj = navigation?.[cemoduleId]?.[pagePath];
  return navObj?.creatorId;
}

const checkIsUGCPage = (creatorId) => {
  const isNil = (value) => value === null || value === undefined;
  return !isNil(creatorId);
};

// Function to check if the content at a specific index is within a <table> tag
const isInTableTag = (beforeMatchIndex, html) => {
  const tableRegex = /<table\b[^>]*>/gi;
  const closeTableRegex = /<\/table>/gi;

  const htmlStringBeforeMatch = html.substring(0, beforeMatchIndex);

  const numberOfTableTags = (htmlStringBeforeMatch.match(tableRegex) || []).length;
  const numberOfCloseTableTags = (htmlStringBeforeMatch.match(closeTableRegex) || []).length;

  // If the number of <table> tags is not equal to the number of </table> tags, then the content is enclosed in <table> tag
  return numberOfTableTags !== numberOfCloseTableTags;
};

const checkIsInValidTag = (html) => {
  const checkIsCloseBeforeOpen = (tagName) => {
    const closeRegex = new RegExp(`</${tagName}>`, 'g');
    const openRegex = new RegExp(`<${tagName}[^>]*>`, 'g');
    // get index
    const closeIndex = html.search(closeRegex);
    const openIndex = html.search(openRegex);
    // check if close tag is before open tag
    return closeIndex < openIndex;
  };
  return (
    checkIsCloseBeforeOpen('strong') || checkIsCloseBeforeOpen('em') || checkIsCloseBeforeOpen('u')
  );
};

// POL-2226 - Automatically convert subheading formatting to appear as headings (use same format as clients can use in the UGC section)
const convertFormatTagsToHeadings = (html, creatorId) => {
  const isUGCPage = checkIsUGCPage(creatorId);
  if (isUGCPage) {
    return html;
  }

  // Define the rules for tag-to-heading conversion
  const tagToHeadingRules = [
    { regex: /<p><strong><u>((?:(?!<\/?em>|<\/?u>|<\/?strong>).)*)<\/u><\/strong><\/p>/g },
    { regex: /<p><strong>((?:(?!<\/?em>|<\/?u>|<\/?strong>).)*)<\/strong><\/p>/g },
    { regex: /<p><u>((?:(?!<\/?em>|<\/?u>|<\/?strong>).)*)<\/u><\/p>/g },
    { regex: /<p><em><u>((?:(?!<\/?em>|<\/?u>|<\/?strong>).)*)<\/u><\/em><\/p>/g },
    { regex: /<p><em>((?:(?!<\/?em>|<\/?u>|<\/?strong>).)*)<\/em><\/p>/g }
    // Add more rules for h6, h7, etc. as needed
  ];

  // Apply the rules to the HTML
  let transformedHTML = html;
  let headingCounter = 0;
  tagToHeadingRules.forEach((rule, indexOfRule) => {
    const headingRuleExists = rule.regex.exec(transformedHTML);
    if (headingRuleExists) {
      headingCounter += 1;
      const replacementTemplate = `<p><h${headingCounter}>$1</h${headingCounter}></p>`;

      // replace tagToHeadingRules with the updated heading level
      tagToHeadingRules.splice(indexOfRule, 1, {
        ...tagToHeadingRules[indexOfRule],
        headingLevel: headingCounter
      });

      const replacer = (originalMatch, captureGroup1, matchIndex) => {
        // captureGroup1 refers to $1 in the replacementTemplate

        // 1. Check if the content ends with a full stop
        const pureTextContent = captureGroup1.replace(/<[^>]*>/g, '').trim();
        const regexForFullStop = /\.$/;
        const contentEndsWithFullStop = regexForFullStop.test(pureTextContent);

        // 2. Check if the content is within a <table> tag
        const contentIsInTableTag = isInTableTag(matchIndex, transformedHTML);

        if (contentEndsWithFullStop || contentIsInTableTag) {
          // POL-2308
          // IF the content ends with a full stop OR IF the content is enclosed within a <table> tag,
          // return the original content without replacement
          return originalMatch;
        }

        const formatTagRegex = /<strong>|<\/strong>|<em>|<\/em>|<u>|<\/u>/g;
        const firstMatch = formatTagRegex.exec(captureGroup1); // check if the content has any formatting tags
        if (firstMatch) {
          // 3. Check if first format tag found is a closing one
          const firstFormatTagIsClosing = firstMatch[0].includes('/');

          if (firstFormatTagIsClosing) {
            // if the first format tag is a closing one, exclude from conversion to prevent error like this:
            // rawHtml: <p><em><u>Content</u></em> <strong>3</strong> <em><u>– Italic and Underline     </u></em></p>
            // convertedHtml: <p><h3>Content</u></em> <strong>3</strong> <em><u>– Italic and Underline</h3></p>
            return originalMatch;
          }
          // 4. Check if the format tag has proper closing tag (bold, underline, italic)
          const isInValidTag = checkIsInValidTag(captureGroup1);
          if (isInValidTag) {
            return originalMatch;
          }
          // remove any format tags in formatTagRegex
          const contentWithoutFormatTag = captureGroup1.replace(formatTagRegex, '').trim();
          return replacementTemplate.replace('$1', contentWithoutFormatTag);
        }

        return replacementTemplate.replace('$1', captureGroup1);
      };
      const htmlWithHeadingLevel = transformedHTML.replace(rule.regex, replacer);

      transformedHTML = htmlWithHeadingLevel;
    }
  });
  return transformedHTML;
};

const getUniqueElementId = (prefix = 'heading-uId') => {
  return `${prefix}-${uniqueId()}`;
};

const getHeadingLevelFromHeadingString = (headingTag) => {
  const headingLevel = parseInt(headingTag.match(/<h([1-5])/)[1], 10);
  return headingLevel;
};

const getUnescapedTextFromHtmlString = (text) => {
  const tooltipRemovedText = removeTooltipSpanTag(text);
  const unescapedText = htmlUnescape(striptags(tooltipRemovedText)).trim();
  if (unescapedText) {
    return unescapedText;
  }
  return null;
};

/**
 * Add Anchor for html
 * @param {String: html}
 * @returns {Object: {String: anchoredHTML, Array: anchorIds}}
 */
function addAnchorLinkForHTML(html) {
  const headingRegex = /(<h[1-5]>.+?<\/h[1-5]>)/g;

  const anchorIds = [];

  const htmlWithHeadingId = html?.replace(headingRegex, (match) => {
    try {
      const headingLevel = getHeadingLevelFromHeadingString(match);
      const unescapedText = getUnescapedTextFromHtmlString(match);
      if (!unescapedText) {
        return match; // If unescaped text is null or empty, return the original match
      }
      const text = unescapedText.replace(/&nbsp;/g, ' ');
      const uniqueElementId = getUniqueElementId();
      const htmlTagWithId = match.replace(
        /<h[1-5]>(.+?)<\/h[1-5]>/,
        (m, innerText) =>
          `<h${headingLevel} id="${uniqueElementId}">${innerText}</h${headingLevel}>`
      );

      anchorIds.push({
        text,
        id: uniqueElementId,
        headingLevel
      });
      return htmlTagWithId;
    } catch (error) {
      console.error('Error in inner replace callback:', error);
      return match; // Return the original match if any error occurs
    }
  });
  return { anchorIds, htmlWithHeadingId };
}

export {
  addAnchorLinkForHTML,
  convertFormatTagsToHeadings,
  getCreatorId,
  parseFromString,
  removeTooltipSpanTag,
  unescape
};
